import React, { useEffect } from 'react';
import { Navigate, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import WorkShiftCalendar from './WorkShiftCalendar';
import AddDailyWorkShift from './AddDailyWorkShift';
import SalarySlip from './SalarySlip';
import { TeamworkKanban } from './kanban';

type TabType = 'shifts' | 'add-shift' | 'salary-slip' | 'teamwork';

// localStorage key for persisting year/month selection
const WORK_DATE_STORAGE_KEY = 'work-selected-date';

interface TabProps {
  id: TabType;
  labelKey: string;
  icon: React.ReactNode;
}

const tabsConfig: TabProps[] = [
  {
    id: 'shifts',
    labelKey: 'tabs.schedules',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
  },
  {
    id: 'add-shift',
    labelKey: 'tabs.addShift',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
      </svg>
    ),
  },
  {
    id: 'salary-slip',
    labelKey: 'tabs.salarySlip',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
  },
  {
    id: 'teamwork',
    labelKey: 'tabs.teamwork',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
      </svg>
    ),
  },
];

const WorkContainer: React.FC = () => {
  const { t } = useTranslation('work');
  const { isAuthenticated, isLoading } = useAuth();
  const { tab } = useParams<{ tab: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Default to 'shifts' if no tab specified
  const activeTab = (tab as TabType) || 'shifts';

  // Get stored date from localStorage
  const getStoredDate = (): { year: number; month: number } => {
    try {
      const stored = localStorage.getItem(WORK_DATE_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.year && parsed.month) {
          return parsed;
        }
      }
    } catch {
      // Ignore parse errors
    }
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() + 1 };
  };

  // Initialize year/month from URL params, localStorage, or current date
  const getInitialYear = (): number => {
    const urlYear = searchParams.get('year');
    if (urlYear) return parseInt(urlYear, 10);
    return getStoredDate().year;
  };

  const getInitialMonth = (): number => {
    const urlMonth = searchParams.get('month');
    if (urlMonth) return parseInt(urlMonth, 10);
    return getStoredDate().month;
  };

  const selectedYear = getInitialYear();
  const selectedMonth = getInitialMonth();

  // Persist to localStorage and update URL when year/month changes
  useEffect(() => {
    localStorage.setItem(WORK_DATE_STORAGE_KEY, JSON.stringify({ year: selectedYear, month: selectedMonth }));

    // Update URL if not already set
    const urlYear = searchParams.get('year');
    const urlMonth = searchParams.get('month');
    if (urlYear !== selectedYear.toString() || urlMonth !== selectedMonth.toString()) {
      setSearchParams({ year: selectedYear.toString(), month: selectedMonth.toString() }, { replace: true });
    }
  }, [selectedYear, selectedMonth, searchParams, setSearchParams]);

  // Handler to update year/month (updates URL which triggers re-render)
  const handleDateChange = (year: number, month: number) => {
    const currentUser = searchParams.get('user');
    const newParams: Record<string, string> = { year: year.toString(), month: month.toString() };
    if (currentUser) {
      newParams.user = currentUser;
    }
    setSearchParams(newParams);
  };

  // Get selected user from URL (for admin viewing other users)
  const selectedUser = searchParams.get('user') || undefined;

  // Handler to update selected user (updates URL which triggers re-render)
  const handleUserChange = (username: string) => {
    setSearchParams({
      year: selectedYear.toString(),
      month: selectedMonth.toString(),
      user: username,
    });
  };

  // Wait for auth to load before redirecting
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  // Redirect to home if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/app" replace />;
  }

  // Redirect to /app/work/shifts if accessing /app/work without a tab
  if (!tab) {
    const queryString = searchParams.toString();
    return <Navigate to={`/app/work/shifts${queryString ? `?${queryString}` : ''}`} replace />;
  }

  // Preserve query string when changing tabs
  const handleTabChange = (tabId: TabType) => {
    const queryString = searchParams.toString();
    navigate(`/app/work/${tabId}${queryString ? `?${queryString}` : ''}`);
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'shifts':
        return (
          <WorkShiftCalendar
            selectedYear={selectedYear}
            selectedMonth={selectedMonth}
            onDateChange={handleDateChange}
            selectedUser={selectedUser}
            onUserChange={handleUserChange}
          />
        );
      case 'add-shift':
        return (
          <AddDailyWorkShift
            selectedYear={selectedYear}
            selectedMonth={selectedMonth}
          />
        );
      case 'salary-slip':
        return (
          <SalarySlip
            selectedYear={selectedYear}
            selectedMonth={selectedMonth}
            onDateChange={handleDateChange}
          />
        );
      case 'teamwork':
        return <TeamworkKanban />;
      default:
        return (
          <WorkShiftCalendar
            selectedYear={selectedYear}
            selectedMonth={selectedMonth}
            onDateChange={handleDateChange}
            selectedUser={selectedUser}
            onUserChange={handleUserChange}
          />
        );
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md dark:shadow-gray-700 p-4 sm:p-6">
        <div className="flex items-center">
          <div className="p-2 sm:p-3 bg-purple-100 dark:bg-purple-900 rounded-full">
            <svg className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <div className="ml-3 sm:ml-4">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">{t('title')}</h1>
            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">{t('subtitle')}</p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md dark:shadow-gray-700">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex -mb-px overflow-x-auto scrollbar-hide snap-x snap-mandatory">
            {tabsConfig.map((tabItem) => (
              <button
                key={tabItem.id}
                onClick={() => handleTabChange(tabItem.id)}
                className={`snap-start flex-shrink-0 flex items-center px-3 sm:px-6 py-3 sm:py-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap min-h-[44px] ${
                  activeTab === tabItem.id
                    ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <span className={activeTab === tabItem.id ? 'text-purple-600 dark:text-purple-400' : 'text-gray-400 dark:text-gray-500'}>
                  {tabItem.icon}
                </span>
                <span className="ml-2 hidden xs:inline">{t(tabItem.labelKey)}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-3 sm:p-6">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default WorkContainer;
